/*
 * @providesModule RequestCoalescer
 *
 */

/**
 * Shared request coalescing utility for NetClient
 *
 * This utility handles caching and coalescing of GET requests across
 * all platform implementations (web).
 * !!!!
 * NOTE: THERE IS A SEPARATE COALESCER FOR iOS AND ANDROID!
 * !!!!
 */

import {datadogRum} from '@datadog/browser-rum';

// Default cache timeout - requests within this window are coalesced
const DEFAULT_CACHE_TIMEOUT_MS = 1000;

interface CacheEntry {
  promise: Promise<unknown>;
  timestamp: number;
  requestStartTime: number;
  actualDurationMs?: number;
  result?: unknown;
  error?: unknown;
}

export class RequestCoalescer {
  private requestCache: Map<string, CacheEntry> = new Map();
  private cacheTimeoutMs: number = DEFAULT_CACHE_TIMEOUT_MS;

  // Metrics tracking
  private totalRequests: number = 0;
  private coalescedRequests: number = 0;
  private totalTimeSavedMs: number = 0;

  /**
   * Generate a cache key for GET requests based on URL and parameters
   * Note: Headers are NOT included as they can change frequently (location, timestamps, etc.)
   */
  generateCacheKey(url: string, params: Record<string, string>): string {
    // Start with the base URL
    let cacheKey = url;

    // Add parameters if they exist
    if (params && typeof params === 'object') {
      // Convert to string values and sort keys to ensure consistent cache keys
      const sortedEntries = Object.entries(params)
        .map(([key, value]) => [key, String(value)] as [string, string])
        .sort(([a], [b]) => a.localeCompare(b));

      if (sortedEntries.length > 0) {
        const urlParams = new URLSearchParams(sortedEntries);
        const queryString = urlParams.toString();
        if (queryString) {
          cacheKey += '?' + queryString;
        }
      }
    }

    return cacheKey;
  }

  /**
   * Check if a request should be coalesced and return existing promise if so
   */
  checkCoalescedRequest(url: string, params: Record<string, string>): Promise<unknown> | null {
    this.cleanupExpiredCache();
    this.totalRequests++;

    const cacheKey = this.generateCacheKey(url, params);
    const cachedEntry = this.requestCache.get(cacheKey);

    if (cachedEntry) {
      const timeSinceRequest = Date.now() - cachedEntry.timestamp;

      // If the cached request is still within the timeout window
      if (timeSinceRequest < this.cacheTimeoutMs) {
        this.coalescedRequests++;

        // Calculate actual time saved based on when this coalesced request would have started
        const coalescedRequestStartTime = Date.now();

        // Return a promise that calculates actual time saved when the original request completes
        const coalescedPromise = cachedEntry.promise
          .then((result) => {
            const originalRequestEndTime = Date.now();
            const actualTimeSaved = originalRequestEndTime - coalescedRequestStartTime;

            this.totalTimeSavedMs += actualTimeSaved;

            // Log coalesced request with actual time saved
            console.debug(
              `RequestCoalescer: Coalesced request to ${url}. Saved ${actualTimeSaved}ms. Total saved: ${this.coalescedRequests} requests, ${this.totalTimeSavedMs}ms`
            );

            // Log periodic metrics summary (every 10th coalesced request)
            if (this.coalescedRequests % 10 === 0) {
              const cacheHitRate =
                this.totalRequests > 0 ? (this.coalescedRequests / this.totalRequests) * 100 : 0;

              // Send metrics to Datadog
              datadogRum.addAction('request_coalescer_metrics', {
                total_requests: this.totalRequests,
                coalesced_requests: this.coalescedRequests,
                cache_hit_rate: Math.round(cacheHitRate * 100) / 100,
                time_saved_ms: this.totalTimeSavedMs,
                time_saved_seconds: parseFloat((this.totalTimeSavedMs / 1000).toFixed(1)),
                platform: 'web',
              });

              console.debug(
                `RequestCoalescer: Periodic metrics - Total: ${this.totalRequests}, Coalesced: ${
                  this.coalescedRequests
                }, Hit Rate: ${Math.round(cacheHitRate * 100) / 100}%, Time Saved: ${(
                  this.totalTimeSavedMs / 1000
                ).toFixed(1)}s`
              );
            }

            return result;
          })
          .catch((error) => {
            // Still log the time saved even if the request failed
            const originalRequestEndTime = Date.now();
            const actualTimeSaved = originalRequestEndTime - coalescedRequestStartTime;
            this.totalTimeSavedMs += actualTimeSaved;

            console.debug(
              `RequestCoalescer: Coalesced request to ${url} failed but still saved ${actualTimeSaved}ms`
            );

            throw error;
          });

        return coalescedPromise;
      } else {
        // Remove expired entry
        this.requestCache.delete(cacheKey);
      }
    }

    return null;
  }

  /**
   * Cache a new request promise
   */
  cacheRequest(url: string, params: Record<string, string>, promise: Promise<unknown>): void {
    const cacheKey = this.generateCacheKey(url, params);
    const now = Date.now();

    // Wrap the promise to track completion time and remove from cache on error
    const wrappedPromise = promise
      .then((result) => {
        const requestDuration = Date.now() - now;
        console.debug(`RequestCoalescer: Request to ${url} completed in ${requestDuration}ms`);

        // Update cache entry with actual duration
        const cacheEntry = this.requestCache.get(cacheKey);
        if (cacheEntry) {
          cacheEntry.actualDurationMs = requestDuration;
        }

        return result;
      })
      .catch((error) => {
        this.requestCache.delete(cacheKey);
        // Requests fired without a game/unity context OR a session (e.g. the
        // anonymous partner catalogue's background badge/LTO fetches, which 401)
        // are expected to fail and always use failSilently — log at debug, not
        // error, so they don't spam the console. The error still re-throws to the
        // caller, so genuine failures are surfaced by whoever actually awaits.
        const message = error instanceof Error ? error.message : String(error);
        // NetClient rejects with the Response, which carries `.code`/`.status`.
        const status = (error && (error.code ?? error.status)) as number | undefined;
        if (/no game context|unity config not set/i.test(message) || status === 401 || status === 403) {
          console.debug('RequestCoalescer: background request failed (expected):', message || status);
        } else {
          console.error('RequestCoalescer: Error caching request:', error);
        }
        throw error;
      });

    // The cached copy is only consumed when a LATER request coalesces onto it; if
    // none does within the window, its rejection would surface as an "Uncaught (in
    // promise)" even though the original caller (which received the pre-wrap promise)
    // handled its own copy. A detached no-op catch marks THIS copy handled without
    // swallowing the error for real coalesced consumers, which attach their own
    // .then/.catch via checkCoalescedRequest.
    wrappedPromise.catch(() => {});

    this.requestCache.set(cacheKey, {
      promise: wrappedPromise,
      timestamp: now,
      requestStartTime: now,
    });

    console.debug(`RequestCoalescer: Cached new request to ${url}`);
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpiredCache(): void {
    const now = Date.now();
    Array.from(this.requestCache.entries()).forEach(([key, entry]) => {
      if (now - entry.timestamp > this.cacheTimeoutMs) {
        this.requestCache.delete(key);
      }
    });
  }

  /**
   * Clear all cached requests (useful for testing)
   */
  clearCache(): void {
    this.requestCache.clear();
  }
}
